import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { preconApi } from "../lib/api";

/** BID-1 §2/§3 — Status (what kind of bid) and Stage (where it is) chips; colours per the brief. */
export const STATUS_CHIP = {
  Qualify: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-200",
  "No Bid": "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200",
  "New Bid": "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200",
  Rebid: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200",
  Budget: "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200",
};
export const STAGE_CHIP = {
  Qualify: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  Estimating: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  "Bid Submitted": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  "Budget Submitted": "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
  Reconcile: "bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200",
  Archived: "bg-muted text-muted-foreground",
};
export const BID_STATUSES = ["Qualify", "No Bid", "New Bid", "Rebid", "Budget"];
export const NO_BID_REASONS = ["Not a good fit", "Bandwidth", "Schedule issues", "Limited scope", "Location", "Other"];

// Single board lifecycle (Status column). Order matches the backend BOARD_STATUSES.
export const BOARD_STATUSES = ["Qualify", "No Bid", "New Bid", "Started", "Rebid", "Budget",
  "Submitted", "Won", "Lost", "Reconcile", "Cancelled"];
// Won / Lost / Cancelled are set on the Pipeline only (it pushes them to the board).
export const PIPELINE_ONLY = ["Won", "Lost", "Cancelled"];
export const BOARD_STATUS_CHIP = {
  Qualify: "bg-yellow-100 text-yellow-800 border-yellow-300",
  "No Bid": "bg-red-100 text-red-800 border-red-300",
  "New Bid": "bg-emerald-100 text-emerald-800 border-emerald-300",
  Started: "bg-teal-100 text-teal-800 border-teal-300",
  Rebid: "bg-sky-100 text-sky-800 border-sky-300",
  Budget: "bg-purple-100 text-purple-800 border-purple-300",
  Submitted: "bg-blue-100 text-blue-800 border-blue-300",
  Won: "bg-emerald-200 text-emerald-900 border-emerald-500 font-semibold",
  Lost: "bg-rose-100 text-rose-800 border-rose-300",
  Reconcile: "bg-pink-100 text-pink-700 border-pink-300",
  Cancelled: "bg-slate-200 text-slate-700 border-slate-400",
};

/** The one Status control — sets the board lifecycle; No Bid and Lost collect a reason. */
export function BoardStatusChip({ job, canEdit, onChanged, allJobs = [] }) {
  const [noBid, setNoBid] = useState(false);
  const current = job.board_status || "Qualify";
  const change = async (v) => {
    if (v === current) return;
    if (v === "No Bid") { setNoBid(true); return; }
    if (v === "Lost") {
      const reason = window.prompt(`Why was ${job.project_name} lost?`);
      if (!reason || !reason.trim()) { toast.error("Lost needs a reason"); return; }
      try {
        await preconApi.setBoardStatus(job.id, { status: "Lost", reason_text: reason.trim() });
        toast.success(`${job.project_name}: Lost`); onChanged?.();
      } catch (e) { toast.error(e?.response?.data?.detail || "Status did not save"); }
      return;
    }
    if (v === "Cancelled" && !window.confirm(`Mark ${job.project_name} as Cancelled? It is archived with outcome Cancelled (reversible from the archive).`)) return;
    const body = { status: v };
    if (["New Bid", "Rebid", "Budget", "Started"].includes(v)) {
      try {
        const o = await preconApi.overload(job.id);
        if (o.overloaded) {
          const list = o.bids.map((b) => b.project_name).join(", ");
          if (!window.confirm(`${o.estimator} has ${o.count} bids due ${o.date} — this one plus ${list}. Proceed?`)) return;
          await preconApi.overloadDecision(job.id, { decision: "proceed", count: o.count, date: o.date });
        }
      } catch { /* advisory only */ }
    }
    if (v === "Rebid") {
      const prior = allJobs.filter((j) => j.id !== job.id && j.normalized_name === job.normalized_name);
      if (prior.length) body.prior_bid_id = prior[0].id;
    }
    try {
      const r = await preconApi.setBoardStatus(job.id, body);
      toast.success(`${job.project_name}: ${r.board_status || v}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status did not save"); }
  };
  return (
    <>
      {canEdit ? (
        <select value={current} onChange={(e) => change(e.target.value)} onClick={(e) => e.stopPropagation()}
                className={`h-6 rounded border px-1 text-[10px] font-medium ${BOARD_STATUS_CHIP[current] || ""}`}
                data-testid={`board-status-select-${job.id}`} data-status={current}>
          {BOARD_STATUSES.filter((s) => !PIPELINE_ONLY.includes(s) || s === current).map((s) => (
            <option key={s} value={s} disabled={PIPELINE_ONLY.includes(s)}>{s}</option>))}
        </select>
      ) : (
        <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${BOARD_STATUS_CHIP[current] || ""}`}
              data-testid={`board-status-chip-${job.id}`}>{current}</span>
      )}
      {noBid && <NoBidDialog job={job} onClose={() => setNoBid(false)} onDone={() => { setNoBid(false); onChanged?.(); }} />}
    </>
  );
}

export function StageChip({ job }) {
  const label = job.stage === "Archived" && job.outcome ? `Archived · ${job.outcome}` : job.stage;
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${STAGE_CHIP[job.stage] || "bg-muted"}`}
          data-testid={`stage-chip-${job.id}`} data-stage={job.stage}
          title={job.stage === "Archived" && job.no_bid?.reason ? `No Bid — ${job.no_bid.reason}` : job.stage}>
      {label}
    </span>
  );
}

/** Status chip that opens a select for estimator / AM. No Bid routes through the dialog. */
export function StatusChip({ job, canEdit, onChanged, allJobs = [] }) {
  const [noBid, setNoBid] = useState(false);
  const change = async (v) => {
    if (v === "No Bid") { setNoBid(true); return; }
    const body = { bid_status: v };
    if (["New Bid", "Rebid", "Budget"].includes(v)) {   // BID-2 §2 — overload warning
      try {
        const o = await preconApi.overload(job.id);
        if (o.overloaded) {
          const list = o.bids.map((b) => b.project_name).join(", ");
          const proceed = window.confirm(`${o.estimator} has ${o.count} bids due ${o.date} — this one plus ${list}. Proceed?\n\nOK = Proceed · Cancel = Reassign`);
          if (proceed) await preconApi.overloadDecision(job.id, { decision: "proceed", count: o.count, date: o.date });
          else {
            const people = await preconApi.assignable();
            const names = people.estimators.map((p, i) => `${i + 1}. ${p.name || p.email}`).join("\n");
            const pick = window.prompt(`Reassign to (number):\n${names}`);
            const est = people.estimators[parseInt(pick, 10) - 1];
            if (!est) return;
            await preconApi.overloadDecision(job.id, { decision: "reassign", estimator_email: est.email, estimator_name: est.name || "", count: o.count, date: o.date });
          }
        }
      } catch { /* the warning is advisory; the status change still proceeds */ }
    }
    if (v === "Rebid") {
      const prior = allJobs.filter((j) => j.id !== job.id && j.normalized_name === job.normalized_name);
      if (prior.length) body.prior_bid_id = prior[0].id;
    }
    try {
      const r = await preconApi.setBidStatus(job.id, body);
      toast.success(`${job.project_name}: ${v}${r.estimate_id ? " · estimate created" : ""}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status did not save"); }
  };
  return (
    <>
      {canEdit ? (
        <select value={job.bid_status || "Qualify"} onChange={(e) => change(e.target.value)} onClick={(e) => e.stopPropagation()}
                className={`h-6 rounded border px-1 text-[10px] font-medium ${STATUS_CHIP[job.bid_status] || ""}`}
                data-testid={`status-select-${job.id}`} data-status={job.bid_status}>
          {BID_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : (
        <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CHIP[job.bid_status] || ""}`}
              data-testid={`status-chip-${job.id}`}>{job.bid_status}</span>
      )}
      {noBid && <NoBidDialog job={job} onClose={() => setNoBid(false)} onDone={() => { setNoBid(false); onChanged?.(); }} />}
    </>
  );
}

export function NoBidDialog({ job, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [text, setText] = useState("");
  const [action, setAction] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const hasPortal = (job.invitations || []).some((i) => i.decline_link || i.portal_link);
  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    if (reason === "Other" && !text.trim()) { toast.error("Say why"); return; }
    setBusy(true);
    try {
      const r = await preconApi.setBidStatus(job.id, { bid_status: "No Bid", reason, reason_text: text, decline_action: action });
      if (r.decline_link) window.open(r.decline_link, "_blank", "noopener");
      if (r.decline_draft) { setDraft(r.decline_draft); toast.success("No Bid recorded — review the decline draft"); return; }
      toast.success(r.am_review_until ? "No Bid — in the AM review window" : "No Bid — archived");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No Bid did not save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && (draft ? onDone() : onClose())}>
      <DialogContent className="max-w-md" data-testid="no-bid-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="text-base">No Bid — {job.project_name}</DialogTitle></DialogHeader>
        {draft ? (
          <div className="space-y-2 text-xs" data-testid="no-bid-decline-draft">
            <div className="text-muted-foreground">Decline email draft (review before send — nothing has gone out)</div>
            <div><span className="text-muted-foreground">To:</span> {draft.to || "— no GC contact email on this bid"}</div>
            <div><span className="text-muted-foreground">Subject:</span> {draft.subject}</div>
            <pre className="whitespace-pre-wrap rounded border border-border bg-muted/30 p-2 font-sans">{draft.body}</pre>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={onDone} data-testid="no-bid-draft-close">Close</Button>
              <Button variant="internal" size="sm" disabled={!draft.to} data-testid="no-bid-draft-mailto"
                      onClick={() => { window.location.href = `mailto:${draft.to}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`; onDone(); }}>
                Open in mail
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3 text-xs">
              <label className="block text-muted-foreground">Reason (required)
                <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="no-bid-reason"
                        className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm text-foreground">
                  <option value="">— pick one —</option>
                  {NO_BID_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="block text-muted-foreground">Note {reason === "Other" ? "(required)" : "(optional)"}
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid="no-bid-text" className="mt-1" />
              </label>
              <div className="text-muted-foreground">Decline action</div>
              <div className="flex gap-3" data-testid="no-bid-actions">
                <label className="flex items-center gap-1"><input type="radio" name="nb" checked={action === ""} onChange={() => setAction("")} data-testid="no-bid-action-none" /> None</label>
                <label className={`flex items-center gap-1 ${hasPortal ? "" : "opacity-40"}`} title={hasPortal ? "" : "This ITB did not come from a portal"}>
                  <input type="radio" name="nb" disabled={!hasPortal} checked={action === "portal"} onChange={() => setAction("portal")} data-testid="no-bid-action-portal" /> Decline in portal</label>
                <label className="flex items-center gap-1"><input type="radio" name="nb" checked={action === "email"} onChange={() => setAction("email")} data-testid="no-bid-action-email" /> Draft decline email</label>
              </div>
              <p className="text-[11px] text-muted-foreground">The bid's AM gets a review window (Settings › Sales) before it archives.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={onClose} data-testid="no-bid-cancel">Cancel</Button>
              <Button size="sm" onClick={submit} disabled={busy} data-testid="no-bid-submit">{busy ? "Saving…" : "Set No Bid"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function countdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.round(ms / 3600000);
  return ms < 3600000 ? "<1hr" : `${h}h`;
}
